/**
 * Brief 26a - Compliance Register / Form detail (read-only)
 *
 * Renders one register or form: header, tier, governing policy, and the
 * schema + entries (registers) or field definition (forms). In 26a the
 * schema binding is not yet operative, so schema/entries surface as an
 * explicit "not yet defined" state rather than being faked.
 *
 * Scope: 26a Phase 1 only. No edit/write affordances.
 */

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, AlertCircle, RefreshCw, ClipboardList, FileText, ShieldOff, ShieldAlert, ArrowRight } from "lucide-react";
import { authFetch } from "../../config/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

// BM-H3300 (30 Sep 2026): the columns of each register CaseMinder holds,
// read from its records by backend/compliance/firm_registers.py.
// BM-H4600 (23 Oct 2026): a FORF's risk_factors arrive as an object of
// factor -> rating ({complexity: "high", ...}); React cannot render an object,
// so REG-06 crashed. Name the factors rated High (else any rated above Low).
const factorName = (k) => k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, " ");
const text = (v) => {
  if (Array.isArray(v)) return v.map((x) => (x && typeof x === "object" ? text(x) : x)).filter(Boolean).join(", ");
  if (v && typeof v === "object") {
    const rated = Object.entries(v).filter(([, r]) => typeof r === "string");
    const high = rated.filter(([, r]) => r.toLowerCase() === "high");
    const shown = high.length ? high : rated.filter(([, r]) => r.toLowerCase() !== "low");
    return shown.map(([k, r]) => `${factorName(k)}: ${r}`).join(", ");
  }
  return v || "";
};
// Anything still not text is shown as text rather than crashing the page.
const cell = (v) => (v == null || v === false ? "" : typeof v === "object" ? text(v) : v);
const liveOrClosed = (e) => (e.open ? "Live" : "Closed");
export const REGISTER_COLUMNS = {
  // BM-H3370: complaints, held in CaseMinder.
  "REG-01": [
    ["Received", (e) => e.date], ["Reference", (e) => e.entry_ref], ["Matter", (e) => e.matter_ref],
    ["Complainant", (e) => e.complainant], ["Summary", (e) => e.summary],
    ["Acknowledged", (e) => e.acknowledged], ["Responded", (e) => e.responded],
    ["Justified", (e) => (e.justified === true ? "Yes" : e.justified === false ? "No" : "")],
    ["Status", (e) => (e.data_protection ? `${e.status} (data protection)` : e.status)],
  ],
  "REG-02": [
    ["Date", (e) => e.date_entered], ["Matter", (e) => e.matter_ref], ["Client", (e) => e.client],
    ["Fee earner", (e) => e.fee_earner], ["Outcome", (e) => text(e.outcome).replace(/_/g, " ")],
    ["Decided by", (e) => e.decided_by], ["Notes", (e) => e.decision_notes], ["Status", liveOrClosed],
  ],
  "REG-06": [
    ["Entered", (e) => e.date_entered], ["Matter", (e) => e.matter_ref], ["Fee earner", (e) => e.fee_earner],
    ["Why High", (e) => text(e.why_high_risk)],
    ["MLCO decision", (e) => [e.mlco_decision || text(e.mlco_status).replace(/_/g, " "), e.mlco_reason].filter(Boolean).join(": ")],
    ["Next review", (e) => e.next_review], ["Status", liveOrClosed],
  ],
  "REG-09": [
    ["Date", (e) => e.date], ["Matter", (e) => e.matter_ref], ["Override", (e) => e.override_type],
    ["By", (e) => e.by], ["Reason", (e) => e.reason],
    ["Not recorded", (e) => text(e.incomplete)],
  ],
  "REG-10": [
    ["Date", (e) => e.date], ["Matter", (e) => e.matter_ref], ["Reviewer", (e) => e.reviewer],
    ["Status", (e) => e.status],
  ],
};

// BM-H4600: where each row of a register opens. A matter reference opens the
// matter; the row's arrow opens the page where its action is taken.
const isMatterRef = (ref) => typeof ref === "string" && ref.includes(".");
export const matterLink = (ref) => (isMatterRef(ref) ? `/matter/${encodeURIComponent(ref)}` : null);
export function rowLink(ref, e) {
  switch (ref) {
    case "REG-02": return e.entry_ref ? `/conflict-check/${encodeURIComponent(e.entry_ref)}` : matterLink(e.matter_ref);
    case "REG-01": return "/compliance/complaints";
    case "REG-10": return "/compliance/file-reviews";
    case "REG-22": return "/compliance/horizon-scan";
    case "REG-06": case "REG-09": return matterLink(e.matter_ref);
    default: return `/compliance/registers/${encodeURIComponent(ref)}`;
  }
}

const BOOK_COLUMNS = [
  ["Reference", (e) => e.entry_ref],
  ["Added", (e) => e.date],
  ["Matter", (e) => e.matter_ref],
  ["Summary", (e) => e.summary],
  ["Status", (e) => (e.open ? "Live" : "Closed")],
];

function RegisterEntries({ data }) {
  if (data.held_in !== "caseminder") {
    return (
      <section data-testid="artefact-detail-sharepoint" className="text-sm text-gray-600">
        This register is kept in SharePoint (COMPLIANCE). CaseMinder does not read it, so it shows no entries here.
      </section>
    );
  }
  const entries = data.entries || [];
  // BM-H3613: registers held in the register book have no bespoke columns here.
  const cols = REGISTER_COLUMNS[data.ref] || BOOK_COLUMNS;
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-lg font-medium">Entries</h2>
        <span className="text-xs text-gray-500" data-testid="artefact-detail-entry-count">
          {entries.length} {entries.length === 1 ? "entry" : "entries"}, read from CaseMinder's records
        </span>
      </div>
      {data.full_register_link ? (
        <Link to={data.full_register_link} className="text-sm text-blue-700 hover:underline mb-2 inline-block">
          {data.ref === "REG-10" ? "Open the full file review register" : "Open the register"}
        </Link>
      ) : null}
      {entries.length === 0 ? (
        <div data-testid="artefact-detail-entries-empty" className="text-sm text-gray-500">
          CaseMinder's records hold nothing for this register.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="artefact-detail-entries">
            <thead className="bg-gray-50 text-left">
              <tr>
                {cols.map(([h]) => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}
                <th className="px-3 py-2"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={`${e.matter_ref}-${i}`} className={`border-t ${e.incomplete && e.incomplete.length ? "bg-amber-50" : ""}`}>
                  {cols.map(([h, f]) => {
                    const v = cell(f(e));
                    const to = h === "Matter" && v ? matterLink(e.matter_ref) : null;
                    return (
                      <td key={h} className="px-3 py-2 align-top">
                        {to ? <Link to={to} className="text-indigo-600 hover:underline" data-testid="register-row-matter">{v}</Link> : v}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 align-top text-right">
                    {rowLink(data.ref, e) ? (
                      <Link
                        to={rowLink(data.ref, e)}
                        title="Open"
                        aria-label={`Open ${e.matter_ref || e.client || e.entry_ref || "entry"}`}
                        data-testid="register-row-open"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-indigo-600 hover:bg-indigo-50"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function RegisterFormDetail({ kind }) {
  const { ref } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const isRegister = kind === "register";
  const basePath = isRegister ? "/policy/registers" : "/policy/forms";
  const apiPath = isRegister ? "registers" : "forms";
  const Icon = isRegister ? ClipboardList : FileText;

  useEffect(() => {
    if (!ref) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setForbidden(false);
      try {
        const r = await authFetch(
          `${API}/api/policy/${apiPath}/${encodeURIComponent(ref)}`,
          { credentials: "include" }
        );
        if (r.status === 403) {
          if (!cancelled) setForbidden(true);
          return;
        }
        if (r.status === 404) throw new Error(`${isRegister ? "Register" : "Form"} not found`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ref, apiPath, isRegister]);

  const backLink = (
    <div className="mb-4">
      <Link to="/policy/registers" className="text-sm text-blue-700 hover:underline">
        <ArrowLeft className="inline-block mr-1" size={14} />
        Back to Registers &amp; Forms
      </Link>
    </div>
  );

  if (loading) {
    return (
      <div data-testid="artefact-detail-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading {ref}...
      </div>
    );
  }
  if (forbidden) {
    return (
      <div data-testid="artefact-detail-forbidden" className="p-6 text-gray-600">
        <ShieldOff className="inline-block mr-2" size={16} />
        You do not have access to this compliance artefact.
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="artefact-detail-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        {error}
        {backLink}
      </div>
    );
  }
  if (!data) {
    return <div data-testid="artefact-detail-empty" className="p-6">No data.</div>;
  }

  const gp = data.governing_policy || {};
  const schema = data.schema || [];

  return (
    <div data-testid="artefact-detail-page" className="p-6 max-w-4xl mx-auto">
      {backLink}

      <header className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Icon size={20} />
          <h1 className="text-2xl font-semibold" data-testid="artefact-detail-name">
            {data.name}
          </h1>
          <span className="font-mono text-xs text-gray-500">{data.ref}</span>
          {/* BM-H4600: the tier is not a status, so it is an icon, not a colour. */}
          {data.tier === "red" ? (
            <span title="High-stakes register" className="text-indigo-400"><ShieldAlert size={16} /></span>
          ) : null}
        </div>
        <div className="text-sm text-gray-600">
          Governing policy:{" "}
          {gp.slug ? (
            <Link to={`/policy/reader/${gp.slug}`} className="text-blue-700 hover:underline">
              {gp.name || gp.slug}
            </Link>
          ) : (
            data.governing_policy_slug
          )}
          {gp.regulatory_anchor ? (
            <span className="ml-2 text-gray-400">({gp.regulatory_anchor})</span>
          ) : null}
        </div>
      </header>

      {data.narrative_md ? (
        <div
          data-testid="artefact-detail-narrative"
          className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900 whitespace-pre-wrap"
        >
          {data.narrative_md}
        </div>
      ) : null}

      {isRegister ? (
        <RegisterEntries data={data} />
      ) : (
        <section>
          <h2 className="text-lg font-medium mb-2">Definition</h2>
          {schema.length === 0 ? (
            <div data-testid="artefact-detail-schema-empty" className="text-sm text-gray-500">
              Field definition not yet defined (26a Phase 1 — form binding lands in a later phase).
            </div>
          ) : (
            <ul className="list-disc pl-5 text-sm text-gray-700">
              {schema.map((f) => (
                <li key={f.name}>{f.name}</li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
